import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import userService, { PanelTeamTicket } from '../../../shared/services/userService';

const TeamSupport: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { t } = usePageTranslation('panel', 'TeamSupport');

  const isPanelMember = currentUser?.roles_id != null && currentUser.roles_id <= 5;
  const isAdmin = currentUser?.roles_id != null && currentUser.roles_id <= 2;

  const [accessChecked, setAccessChecked] = useState(false);
  /** Référent actif (au moins une fonction) — requis pour ouvrir un ticket */
  const [isReferent, setIsReferent] = useState(false);

  const [tickets, setTickets] = useState<PanelTeamTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<'demande' | 'signalement' | 'question'>('demande');

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await userService.getPanelTeamTickets();
      if (res.success) setTickets(res.data);
    } catch (e: any) {
      setError(e.message || t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentUser) {
        navigate('/');
        return;
      }
      if (!isPanelMember) {
        navigate('/panel');
        return;
      }
      let referentOk = false;
      try {
        const res = await userService.getMyReferents();
        if (!cancelled && res.success && res.data?.length) {
          referentOk = true;
          setIsReferent(true);
        }
      } catch {
        if (!cancelled) setIsReferent(false);
      }
      const canView = isAdmin || referentOk;
      if (!cancelled && !canView) {
        navigate('/panel');
        return;
      }
      if (!cancelled) {
        setAccessChecked(true);
        await load();
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isPanelMember, isAdmin, navigate, load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await userService.createPanelTeamTicket({
        title: title.trim(),
        body: body.trim(),
        category,
      });
      setTitle('');
      setBody('');
      setCategory('demande');
      await load();
    } catch (e: any) {
      setError(e.message || t('createError'));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: number, status: 'open' | 'in_progress' | 'closed') => {
    setSaving(true);
    setError(null);
    try {
      await userService.updatePanelTeamTicketStatus(id, status);
      await load();
    } catch (e: any) {
      setError(e.message || t('updateError'));
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = (s: string) => {
    const key = `status.${s}` as const;
    const v = t(key);
    return v === key ? s : v;
  };

  if (!accessChecked) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <BackgroundImage />
        <ParticlesBackground />
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16 px-4 max-w-5xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">{t('title')}</h1>
            <p className="text-gray-400">{t('subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/panel')}
            className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm shrink-0"
          >
            {t('back')}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/15 border border-red-500/40 text-red-200 text-sm">{error}</div>
        )}

        <div className={`grid gap-8 ${isReferent ? 'lg:grid-cols-2' : ''}`}>
          {isReferent && (
            <Card className="p-6 h-fit">
              <h2 className="text-lg font-bold text-white mb-4">{t('formTitle')}</h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('fieldTitle')}</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('fieldTitlePh')}
                    maxLength={200}
                    className="w-full px-3 py-2 rounded-lg bg-gray-800/60 border border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('fieldCategory')}</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as typeof category)}
                    className="w-full px-3 py-2 rounded-lg bg-gray-800/60 border border-gray-600 text-white"
                  >
                    <option value="demande">{t('catDemande')}</option>
                    <option value="signalement">{t('catSignalement')}</option>
                    <option value="question">{t('catQuestion')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">{t('fieldBody')}</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={t('fieldBodyPh')}
                    rows={6}
                    maxLength={8000}
                    className="w-full px-3 py-2 rounded-lg bg-gray-800/60 border border-gray-600 text-white resize-y min-h-[120px]"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving || !title.trim() || !body.trim()}
                  className="w-full py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold"
                >
                  {saving ? t('submitting') : t('submit')}
                </button>
              </form>
              <p className="mt-4 text-xs text-gray-500">
                {isAdmin ? t('adminHint') : t('authorCloseHint')}
              </p>
            </Card>
          )}

          {!isReferent && isAdmin && (
            <div className="lg:col-span-2 mb-2 rounded-lg border border-gray-600/60 bg-gray-800/30 px-4 py-3 text-sm text-gray-400">
              {t('referentsOnlyForm')}
            </div>
          )}

          <Card className={`p-6 ${!isReferent ? 'lg:col-span-2' : ''}`}>
            <h2 className="text-lg font-bold text-white mb-4">{t('listTitle')}</h2>
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : tickets.length === 0 ? (
              <p className="text-gray-400 text-sm">{t('empty')}</p>
            ) : (
              <ul className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                {tickets.map((tk) => {
                  const mine = tk.user_id === currentUser?.id;
                  return (
                    <li
                      key={tk.id}
                      className="rounded-lg border border-gray-700/80 bg-gray-900/40 p-4 text-sm"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <span className="font-semibold text-white">{tk.title}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded border ${
                            tk.status === 'closed'
                              ? 'border-gray-500 text-gray-400'
                              : tk.status === 'in_progress'
                                ? 'border-blue-500/60 text-blue-300'
                                : 'border-amber-500/50 text-amber-200'
                          }`}
                        >
                          {statusLabel(tk.status)}
                        </span>
                      </div>
                      <p className="text-gray-300 whitespace-pre-wrap mb-2">{tk.body}</p>
                      <p className="text-xs text-gray-500 mb-3">
                        {t('by')} <span className="text-gray-400">{tk.author_username}</span> · {t('date')}{' '}
                        {new Date(tk.created_at).toLocaleString()}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {isAdmin && tk.status === 'open' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'in_progress')}
                            className="px-3 py-1 rounded bg-blue-600/80 hover:bg-blue-600 text-white text-xs disabled:opacity-50"
                          >
                            {t('actions.take')}
                          </button>
                        )}
                        {isAdmin && tk.status === 'in_progress' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'closed')}
                            className="px-3 py-1 rounded bg-gray-600 hover:bg-gray-500 text-white text-xs disabled:opacity-50"
                          >
                            {t('actions.close')}
                          </button>
                        )}
                        {isAdmin && tk.status === 'closed' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'open')}
                            className="px-3 py-1 rounded bg-gray-600 hover:bg-gray-500 text-white text-xs disabled:opacity-50"
                          >
                            {t('actions.reopen')}
                          </button>
                        )}
                        {!isAdmin && mine && tk.status !== 'closed' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'closed')}
                            className="px-3 py-1 rounded bg-gray-600 hover:bg-gray-500 text-white text-xs disabled:opacity-50"
                          >
                            {t('actions.close')}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TeamSupport;
